if (!customElements.get('quantity-popover')) {
  customElements.define(
    'quantity-popover',
    class QuantityPopover extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 990px)');
        this.mqlTablet = window.matchMedia('(min-width: 750px)');
        this.infoButtonDesktop = this.querySelector('.quantity-popover__info-button--icon-only');
        this.infoButtonMobile = this.querySelector('.quantity-popover__info-button--icon-with-label');
        this.popoverInfo = this.querySelector('.quantity-popover__info');
        this.closeButton = this.querySelector('.button-close');
        this.eventMouseEnterHappened = false;

        if (this.closeButton) {
          this.closeButton.addEventListener('click', this.closePopover.bind(this));
        }

        if (this.popoverInfo && this.infoButtonDesktop && this.mqlTablet.matches) {
          this.popoverInfo.addEventListener('mouseleave', this.closePopover.bind(this));
        }

        if (this.infoButtonDesktop) {
          this.infoButtonDesktop.addEventListener('click', this.togglePopover.bind(this));
          this.infoButtonDesktop.addEventListener('focusout', this.closePopover.bind(this));
        }

        if (this.infoButtonMobile) {
          this.infoButtonMobile.addEventListener('click', this.togglePopover.bind(this));
        }

        if (this.infoButtonDesktop && this.mqlTablet.matches) {
          this.infoButtonDesktop.addEventListener('mouseenter', this.togglePopover.bind(this));
          this.infoButtonDesktop.addEventListener('mouseleave', this.closePopover.bind(this));
        }
      }

      togglePopover(event) {
        event.preventDefault();
        if (event.type === 'mouseenter') {
          this.eventMouseEnterHappened = true;
        }

        if (event.type === 'click' && this.eventMouseEnterHappened) return;

        const button = this.infoButtonDesktop && this.mql.matches ? this.infoButtonDesktop : this.infoButtonMobile;
        const isExpanded = button.getAttribute('aria-expanded') === 'true';

        if ((this.mql.matches && !isExpanded) || event.type === 'click') {
          button.setAttribute('aria-expanded', !isExpanded);

          this.popoverInfo.toggleAttribute('hidden');

          button.classList.toggle('quantity-popover__info-button--open');

          this.infoButtonDesktop.classList.add('quantity-popover__info-button--icon-only--animation');
        }

        const isOpen = button.getAttribute('aria-expanded') === 'true';

        if (isOpen && event.type !== 'mouseenter') {
          button.focus();
          button.addEventListener('keyup', (e) => {
            if (e.key === 'Escape') {
              this.closePopover(e);
            }
          });
        }
      }

      closePopover(event) {
        event.preventDefault();
        const isButtonChild = this.infoButtonDesktop.contains(event.relatedTarget);
        const isPopoverChild = this.popoverInfo.contains(event.relatedTarget);

        const button = this.infoButtonDesktop && this.mql.matches ? this.infoButtonDesktop : this.infoButtonMobile;

        if (!isButtonChild && !isPopoverChild) {
          button.setAttribute('aria-expanded', 'false');
          button.classList.remove('quantity-popover__info-button--open');
          this.popoverInfo.setAttribute('hidden', '');
          this.infoButtonDesktop.classList.remove('quantity-popover__info-button--icon-only--animation');
        }

        this.eventMouseEnterHappened = false;
      }
    }
  );
}


/* ============================================================
   VF / Pebble & Wick — Cinematic Quantity Popover Enhancements
   Safe layer on top of Dawn quantity-popover.js.
   Keeps quantity rules tooltip behavior, hover/click opening and ESC closing intact.
   ============================================================ */

(() => {
  const QuantityPopoverElement = customElements.get('quantity-popover');
  if (!QuantityPopoverElement || QuantityPopoverElement.prototype.__vfCinematicPatched) return;

  QuantityPopoverElement.prototype.__vfCinematicPatched = true;

  const originalTogglePopover = QuantityPopoverElement.prototype.togglePopover;
  const originalClosePopover = QuantityPopoverElement.prototype.closePopover;

  const root = document.documentElement;

  const getActiveButton = (popover) =>
    popover.infoButtonDesktop && popover.mql?.matches ? popover.infoButtonDesktop : popover.infoButtonMobile;

  const isPopoverOpen = (popover) => {
    const button = getActiveButton(popover);
    return button?.getAttribute('aria-expanded') === 'true' && !popover.popoverInfo?.hasAttribute('hidden');
  };

  const setQuantityPopoverState = (popover, state, detail = {}) => {
    popover.dataset.vfQuantityPopoverState = state;

    popover.classList.toggle('vf-quantity-popover-open', state === 'open');
    popover.classList.toggle('vf-quantity-popover-closed', state === 'closed');
    popover.classList.toggle('vf-quantity-popover-opening', state === 'opening');
    popover.classList.toggle('vf-quantity-popover-closing', state === 'closing');

    root.classList.toggle('vf-quantity-popover-is-open', state === 'open' || state === 'opening');

    popover.dispatchEvent(
      new CustomEvent(`vf:quantity-popover-${state}`, {
        bubbles: true,
        detail
      })
    );
  };

  const enhanceQuantityPopover = (popover) => {
    if (!popover || popover.__vfCinematicEnhanced) return;
    popover.__vfCinematicEnhanced = true;

    popover.classList.add('vf-quantity-popover-enhanced');

    popover.infoButtonDesktop?.classList.add('vf-quantity-popover__button', 'vf-quantity-popover__button--desktop');
    popover.infoButtonMobile?.classList.add('vf-quantity-popover__button', 'vf-quantity-popover__button--mobile');
    popover.popoverInfo?.classList.add('vf-quantity-popover__panel');
    popover.closeButton?.classList.add('vf-quantity-popover__close');

    [popover.infoButtonDesktop, popover.infoButtonMobile].filter(Boolean).forEach((button) => {
      button.setAttribute('aria-haspopup', 'dialog');
      button.setAttribute('aria-expanded', button.getAttribute('aria-expanded') || 'false');
    });

    if (popover.popoverInfo) {
      popover.popoverInfo.setAttribute('role', popover.popoverInfo.getAttribute('role') || 'dialog');
      popover.popoverInfo.setAttribute('aria-modal', 'false');
    }

    setQuantityPopoverState(popover, isPopoverOpen(popover) ? 'open' : 'closed', {
      initial: true
    });
  };

  const safeCloseFallback = (popover) => {
    const button = getActiveButton(popover);

    button?.setAttribute('aria-expanded', 'false');
    button?.classList.remove('quantity-popover__info-button--open');
    popover.infoButtonDesktop?.classList.remove('quantity-popover__info-button--icon-only--animation');
    popover.infoButtonMobile?.classList.remove('quantity-popover__info-button--open');
    popover.popoverInfo?.setAttribute('hidden', '');
    popover.eventMouseEnterHappened = false;
  };

  QuantityPopoverElement.prototype.togglePopover = function vfTogglePopover(event) {
    enhanceQuantityPopover(this);

    const wasOpen = isPopoverOpen(this);
    setQuantityPopoverState(this, wasOpen ? 'closing' : 'opening', {
      eventType: event?.type,
      trigger: event?.target
    });

    let result;

    try {
      result = originalTogglePopover.call(this, event);
    } catch (error) {
      setQuantityPopoverState(this, 'closed', {
        error
      });
      throw error;
    }

    const open = isPopoverOpen(this);
    const button = getActiveButton(this);

    this.classList.toggle('vf-quantity-popover-hover-opened', event?.type === 'mouseenter' && open);
    this.classList.toggle('vf-quantity-popover-click-opened', event?.type === 'click' && open);
    button?.classList.toggle('vf-quantity-popover__button-active', open);

    window.requestAnimationFrame(() => {
      setQuantityPopoverState(this, open ? 'open' : 'closed', {
        eventType: event?.type,
        trigger: event?.target,
        button
      });
    });

    return result;
  };

  QuantityPopoverElement.prototype.closePopover = function vfClosePopover(event = new Event('close')) {
    enhanceQuantityPopover(this);

    setQuantityPopoverState(this, 'closing', {
      eventType: event?.type,
      trigger: event?.target
    });

    let result;

    try {
      result = originalClosePopover.call(this, event);
    } catch (error) {
      if (error instanceof TypeError) {
        safeCloseFallback(this);
      } else {
        setQuantityPopoverState(this, 'closed', { error });
        throw error;
      }
    }

    const button = getActiveButton(this);
    const open = isPopoverOpen(this);

    button?.classList.toggle('vf-quantity-popover__button-active', open);
    this.classList.remove('vf-quantity-popover-hover-opened', 'vf-quantity-popover-click-opened');

    window.setTimeout(() => {
      setQuantityPopoverState(this, open ? 'open' : 'closed', {
        eventType: event?.type,
        trigger: event?.target,
        button
      });
    }, 180);

    return result;
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('quantity-popover').forEach(enhanceQuantityPopover);
  }, { once: true });

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('quantity-popover button');
    if (!trigger) return;

    trigger.classList.add('vf-quantity-popover-clicked');

    window.setTimeout(() => {
      trigger.classList.remove('vf-quantity-popover-clicked');
    }, 420);
  });

  document.addEventListener('keyup', (event) => {
    if (event.code?.toUpperCase() !== 'ESCAPE') return;

    document.querySelectorAll('quantity-popover').forEach((popover) => {
      if (!isPopoverOpen(popover)) return;

      popover.classList.add('vf-quantity-popover-escape');

      window.setTimeout(() => {
        popover.classList.remove('vf-quantity-popover-escape');
      }, 360);
    });
  });

  const quantityPopoverObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;

        if (node.matches?.('quantity-popover')) {
          enhanceQuantityPopover(node);
        }

        node.querySelectorAll?.('quantity-popover').forEach(enhanceQuantityPopover);
      });
    });
  });

  if (document.body) {
    quantityPopoverObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
})();
